import React, { useEffect, useState } from "react";
import CustomDataTable from "components/customs/table";
import CustomSelect from "components/customs/dropdown";
import { useNavigate } from "react-router";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { APP_URL } from "config";
import { getFormattedOptions, getModifiedObject, Toast } from "utility/helpers";
import CommentsModal from "components/customs/comments-modal";
import {
  deleteRecipient,
  editTotalBudget,
  fetchAgencyDetails,
  fetchProgramComments,
  fetchTable,
  recipientBatchUpdate,
  deleteRecipients,
} from "services/services.projects";
import Swal from "sweetalert2";
import { fetchAllRegions } from "services/services.congressman";
import { genericGet, genericPost } from "services/services.general";
import { defaultTableOption, rowPerPage } from "constants/table";
import { SUPER_ADMIN_ACCOUNTS, ACCOUNT_AGENCY } from "utility/constants";
import { ALLOCATION_STATUS, ALLOCATION_STATUS_OPTIONS } from "constants/status";
import { customProjectStyles } from "constants/style";
import useIsEllipsisActive from "hooks/useIsEllipsisActive";
import usePersistentCountdownTimer from "hooks/usePersistentTimer";
import DohEndorsement from "components/customs/pdf/doh";
import ChedEndorsement from "components/customs/pdf/ched";
import DoleEndorsement from "components/customs/pdf/dole";
import DswdEndorsement from "components/customs/pdf/dswd";
import TesdaEndorsement from "components/customs/pdf/tesda";
import styled from "styled-components";
import EndorsementsAndBudget from "components/customs/endorsementsAndBudget";
import useAuth from "hooks/useAuth";
import LoadingScreen from "components/customs/loading-screen";
import { getProgramSelectorOptions } from "views/projects/utility";
import { checkboxPromptForEndorsingAllocations } from "views/projects/constants";
import ResendModal from "./ResendModal";
import { fetchPendingProjects } from "../../../../redux/actions/projects";
import { ProgramTable } from "./columns";
import ProgramFilters from "./Filters";

const TableSection = styled.div`
  margin-top: 16px;
`;

const CardSection = styled.div`
  background: #ffffff;
  border-radius: 15px;
  box-shadow: 4px 9px 17px -1px #00000014;
`;

const CardContainer = styled.div`
  padding-top: 24px;
  padding-left: 50px;
  padding-right: 50px;
  padding-bottom: 24px;
`;

const FilterSection = styled.div`
  margin-bottom: 16px;
  display: grid;
  grid-template-columns: 500px 250px;
  justify-content: space-between;
  align-items: center;
`;

const Container = styled.div`
  margin-top: 50px;
`;

const ProgramDetails = () => {
  const [tableOption, setTableOption] = useState({
    ...defaultTableOption,
    currentRowsPerPage: rowPerPage,
  });
  const [selectedProject, setSelectedProject] = useState(null);
  const [editingRowId, setEditingRowId] = useState();
  const [initialProjects, setInitialProjects] = useState([]);
  const [agencyDetails, setAgencyDetails] = useState(null);
  const [selectOptions, setSelectOptions] = useState([]);
  const [isModalShown, setIsModalShown] = useState(false);
  const [editingCongressman, setEditingCongressman] = useState("");
  const [comments, setComments] = useState([]);
  const [regions, setRegions] = useState([]);
  const [projectBudget, setProjectBudget] = useState([]);

  const [totalAmountSpent, setTotalAmountSpent] = useState("");
  const [totalBudget, setTotalBudget] = useState("");
  const [remainingBalance, setRemainingBalance] = useState("");
  const [newTotalBudget, setNewTotalBudget] = useState();
  const [checkedItems, setCheckedItems] = useState([]);
  const [isResendModalShown, setIsResendModalShown] = useState(false);
  const [resendData, setResendData] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [resendType, setResendType] = useState("");
  const [messageType, setMessageType] = useState("");

  const [selectedStatusFilter, setSelectedStatusFilter] = useState();

  const [selectedAllocationType, setSelectedAllocationType] = useState();

  const navigate = useNavigate();

  const { id, programID } = useParams();

  const { user } = useAuth();

  const role = user?.type;

  let changeSearchTimeoutID;

  const dateRange = useSelector((state) => state.dateRange);
  const { dateFrom, dateTo } = dateRange || {};

  const dispatch = useDispatch();

  const { isEllipsisUsed } = useIsEllipsisActive();

  const { timer, toggleTimer } = usePersistentCountdownTimer();

  const toggleModal = () => setIsModalShown(!isModalShown);
  const toggleResendModal = () => setIsResendModalShown(!isResendModalShown);

  const handleCancelClicked = (index) => {
    setEditingRowId("");
    setEditingCongressman("");

    const newData = [...tableOption.data];
    newData[index] = initialProjects[index];

    setTableOption({
      ...tableOption,
      data: newData,
    });
  };

  const fetchComments = (congid) => {
    fetchProgramComments({
      params: congid,
      callback: (data) => {
        setComments(data);
      },
    });
  };

  const onChangePage = (page) => {
    // reset editing row
    setEditingRowId();

    setTableOption({
      ...tableOption,
      page,
      offset: tableOption.currentRowsPerPage * page - 100,
    });
  };

  const onChangeRowsPerPage = (currentRowsPerPage, currentPage) => {
    // reset editing row
    setEditingRowId();

    setTableOption({
      ...tableOption,
      currentRowsPerPage,
      page: currentPage,
    });
  };

  const onSort = (column, sortDirection) => {
    // reset editing row
    setEditingRowId();

    setTableOption({
      ...tableOption,
      sort: {
        field: column.identifier,
        direction: sortDirection,
      },
    });
  };

  const onChangeSearch = (event) => {
    event.persist();

    if (changeSearchTimeoutID) {
      clearTimeout(changeSearchTimeoutID);
    }

    changeSearchTimeoutID = setTimeout(() => {
      const eventValue = event.target.value;
      // reset editing row
      setEditingRowId();

      setTableOption({
        ...tableOption,
        page: 1,
        searchValue: eventValue.trim(),
      });
    }, 1000);
  };

  const fetchData = () => {
    setTableOption({
      ...tableOption,
      isLoading: true,
    });

    const params = {
      limit: tableOption.currentRowsPerPage,
      offset: tableOption.offset,
      search: tableOption.searchValue,
      column: tableOption.sort.field,
      order: tableOption.sort.direction,
      startDate: dateFrom || "",
      endDate: dateTo || "",
      type: selectedAllocationType?.map((item) => item.value).join(";"),
    };

    if (role !== ACCOUNT_AGENCY) {
      params.status = selectedStatusFilter?.map((item) => item.value).join(";");
    }

    fetchTable({
      url: `/project/${programID}/recipients/`,
      params,
      callback: (data) => {
        setTableOption({
          ...tableOption,
          data: data?.rows,
          totalRows: data?.count,
          isLoading: false,
        });
        setInitialProjects(data?.rows);
      },
    });
  };

  const fetchAgency = () => {
    fetchAgencyDetails({
      params: id,
      callback: (data) => {
        const options = getProgramSelectorOptions(data?.Projects);
        setSelectOptions(options);
        setAgencyDetails(data);

        options?.forEach((op) => {
          if (op?.ID === Number(programID)) {
            setSelectedProject(op);
          }
        });
      },
      dateRange,
    });
  };

  const fetchBudgetAllocation = () => {
    genericGet({
      url: `/project/${programID}/allocatedBudget`,
      params: {
        startDate: dateFrom || "",
        endDate: dateTo || "",
      },
      callback: (data) => {
        const budgetRecipient = data?.allocatedBudgetPerRecipient;

        const totalAgencyBudget =
          data?.totalBudget !== null ? data?.totalBudget : 0;

        const amountSpent =
          data?.allocatedBudget !== null ? data?.allocatedBudget : 0;
        const remainingBudget = totalAgencyBudget - amountSpent;
        let projs = [];

        setTotalBudget(totalAgencyBudget);
        setTotalAmountSpent(amountSpent);
        setRemainingBalance(remainingBudget);

        if (budgetRecipient?.length !== 0) {
          projs = budgetRecipient?.map((proj, index) => {
            return {
              id: index + 1,
              name: proj?.region,
              value: proj.budget,
              budget:
                proj.budget !== 0 ? (proj.budget / totalAgencyBudget) * 100 : 0,
            };
          });
        }

        projs.push({
          id: 0,
          name: "Remaining Balance",
          value: remainingBudget,
          budget:
            remainingBudget !== 0
              ? (remainingBudget / totalAgencyBudget) * 100
              : 0,
        });

        setProjectBudget(projs);

        if (totalAgencyBudget === 0) setProjectBudget([]);
      },
    });
  };

  const batchUpdate = ({ formData, callback }) => {
    recipientBatchUpdate({
      url: `${APP_URL}/recipient/batch-update/status`,
      formData,
      callback,
    });
  };

  const handleSaveStatusChange = async (data, index) => {
    setTableOption({
      ...tableOption,
      isLoading: true,
    });

    const initialData = initialProjects[index];
    const newData = getModifiedObject(initialData, data);

    const finalData = {};
    let pdf = "";
    finalData.status = data?.status;
    finalData.excludeCongAndCOS = Boolean(data.excludeCongAndCOS);
    finalData.isSpeakerVisible = Boolean(data.showEndorsementToSpeaker);
    finalData.sendOnlyToSuperAdmin = Boolean(data.sendOnlyToSuperAdmin);

    if (data?.status === ALLOCATION_STATUS.APPROVED) {
      const details = data;
      details.program = selectedProject?.Name;
      details.agency = agencyDetails?.name;
      details.agencyAcronym = agencyDetails?.acronym;

      switch (id) {
        case "8":
          pdf = await DohEndorsement(details);
          break;

        case "28":
          pdf = await ChedEndorsement(details);
          break;

        case "11":
          pdf = await DoleEndorsement(details);
          break;

        case "16":
          pdf = await DswdEndorsement(details);
          break;

        case "27":
          pdf = await TesdaEndorsement(details);
          break;

        default:
          break;
      }
    }

    finalData.recipients = [
      {
        recipientId: data?.id,
        endorsementLink: pdf,
        ...newData,
      },
    ];

    batchUpdate({
      formData: finalData,
      callback: ({ isSuccess, msg }) => {
        if (isSuccess) {
          fetchData();

          if (SUPER_ADMIN_ACCOUNTS.includes(role)) {
            dispatch(fetchPendingProjects());
          }
          setSelectedStatus("");
          setResendData([]);

          const text = msg.replace(/\n/g, "<br>");

          Swal.fire({
            title: text.includes("<br>") ? "" : "Success!",
            icon: "success",
            html: text,
            customClass: text.includes("<br>")
              ? { container: "sms-notif" }
              : null,
            confirmButtonText: "OK",
          });
        } else {
          handleCancelClicked(index);

          Toast({
            type: 4,
            content: "An error occurred!",
          });
        }
      },
    });
  };

  const handleRemarks = (text, recipientId, url) => {
    genericPost({
      url: "/comment",
      formData: {
        text,
        recipientId,
        newsId: null,
      },
      callback: () => {
        //
      },
    });

    genericPost({
      url,
      formData: {
        remarks: text,
        recipientId,
      },
      callback: () => {
        //
      },
    });
  };

  const handleDeleteAction = (row, idx) => {
    Swal.fire({
      title: "Warning",
      // eslint-disable-next-line max-len
      text: `Are you sure you want to remove ${row?.recipient?.lastName}, ${row?.recipient?.firstName} from ${selectedProject?.Name}?`,
      icon: "warning",
      confirmButtonText: "OK",
      showCancelButton: true,
    }).then((result) => {
      if (result.isConfirmed) {
        deleteRecipient({
          params: row?.id,
          callback: ({ isSuccess }) => {
            if (isSuccess) {
              Toast({
                type: 1,
                content: "Successfully deleted recipient.",
              });
              fetchData();
            } else {
              Toast({
                type: 4,
                content: "An error occurred.",
              });
            }
          },
        });
      } else if (idx !== undefined && idx !== null) {
        handleSaveStatusChange(row, idx);
      }
    });
  };

  const handleStatusChange = async (index, option) => {
    const newData = [...tableOption.data];
    const oldValue = newData[index].status;
    const newValue = option.value;

    const oldStatus = ALLOCATION_STATUS_OPTIONS.find(
      (status) => status.value === newData[index].status
    ).label;

    if (oldValue.toLowerCase() !== newValue.toLowerCase()) {
      try {
        const checkbox = {};

        if (newValue === ALLOCATION_STATUS.APPROVED) {
          checkbox.html = checkboxPromptForEndorsingAllocations(
            oldStatus,
            option.label,
            role
          );

          checkbox.preConfirm = () => {
            const checkbox2 = document.getElementById("swal-checkbox2")?.checked
              ? 1
              : 0;

            const checkbox3 = document.getElementById("swal-checkbox3")?.checked
              ? 1
              : 0;

            return {
              excludeCongAndCOS: 0,
              showEndorsementToSpeaker: checkbox2,
              sendOnlyToSuperAdmin: checkbox3,
            };
          };
          checkbox.focusConfirm = false;
        }

        const result = await Swal.fire({
          title: "Warning",
          // eslint-disable-next-line max-len
          text: `Are you sure you want to change the status from ${oldStatus} to ${option.label}?`,
          icon: "warning",
          confirmButtonText: "OK",
          showCancelButton: true,
          ...checkbox,
        });

        if (result.isConfirmed) {
          newData[index] = {
            ...newData[index],
            status: newValue,
            excludeCongAndCOS: result.value?.excludeCongAndCOS ?? 0,
            showEndorsementToSpeaker:
              result.value?.showEndorsementToSpeaker ?? 0,
            sendOnlyToSuperAdmin: result.value?.sendOnlyToSuperAdmin ?? 0,
          };

          setTableOption({
            ...tableOption,
            data: newData,
          });

          if (
            newValue === ALLOCATION_STATUS.RESUBMISSION ||
            newValue === ALLOCATION_STATUS.NOTELIGIBLE
          ) {
            const { value: text } = await Swal.fire({
              input: "textarea",
              inputLabel: "Remarks",
              inputPlaceholder: "Type your remarks here...",
              inputAttributes: {
                "aria-label": "Type your remarks here",
              },
              showCancelButton: true,
            });

            if (text) {
              newData[index] = {
                ...newData[index],
                remarks: text,
              };

              const url =
                newValue === ALLOCATION_STATUS.RESUBMISSION
                  ? "/recipient/send-resubmission-notification"
                  : "/recipient/send-not-eligible-notification";
              handleRemarks(text, newData[index].id, url);
            } else return;
          }

          if (
            newValue === ALLOCATION_STATUS.NOTELIGIBLE &&
            SUPER_ADMIN_ACCOUNTS.includes(role)
          ) {
            const res = await Swal.fire({
              title: "Warning",
              text: "Not Eligible allocations are opt to be deleted. Do you wish to proceed?",
              icon: "info",
              confirmButtonText: "OK",
              showCancelButton: true,
            });

            if (res.isConfirmed) {
              handleDeleteAction(newData[index], index);

              return;
            }
          }

          handleSaveStatusChange(newData[index], index);
        } else {
          newData[index] = {
            ...newData[index],
            status: oldValue,
          };

          setTableOption({
            ...tableOption,
            data: newData,
          });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    }
  };

  const fetchRegions = () => {
    fetchAllRegions({
      params: {
        includeCentral: true,
      },
      callback: (data) => {
        const options = getFormattedOptions("acronym", data);

        setRegions(options);
      },
    });
  };

  const handleOverallBudgetChange = (budget) => {
    if (totalBudget !== 0 && budget > agencyDetails?.totalBudget) {
      Swal.fire({
        title: "Error",
        // eslint-disable-next-line max-len
        text: `Recommended Amount for ${selectedProject.Name} exceeds the total recommended amount under ${agencyDetails?.name}`,
        icon: "error",
        confirmButtonText: "OK",
      });
      setNewTotalBudget(totalBudget);

      return;
    }

    editTotalBudget({
      formData: {
        totalBudget: budget,
      },
      url: `/project/${programID}`,
      callback: (response) => {
        const status = response?.status;

        if (status) {
          Toast({
            type: 1,
            content: "Successfully updated total budget",
          });
          fetchBudgetAllocation();
        } else {
          setNewTotalBudget(totalBudget);
        }
      },
    });
  };

  const handleMultipleDelete = async () => {
    setTableOption({
      ...tableOption,
      isLoading: true,
    });
    const projectIds = checkedItems.join(",");

    const result = await Swal.fire({
      title: "Warning",
      // eslint-disable-next-line max-len
      text: "Are you sure you want to remove the selected recipients?",
      icon: "warning",
      confirmButtonText: "OK",
      showCancelButton: true,
    });

    if (result.isConfirmed) {
      deleteRecipients({
        params: projectIds,
        callback: ({ isSuccess }) => {
          if (isSuccess) {
            setCheckedItems([]);
            setSelectedStatus("");

            Toast({
              type: 1,
              content: "Successfully deleted selected recipients.",
            });
            fetchData();

            if (SUPER_ADMIN_ACCOUNTS.includes(role)) {
              dispatch(fetchPendingProjects());
            }
          } else {
            setCheckedItems([]);

            Toast({
              type: 4,
              content: "An error occurred.",
            });
          }
        },
      });
    }

    if (result.isDismissed) {
      setCheckedItems([]);
      setSelectedStatus("");

      setTableOption({
        ...tableOption,
        isLoading: false,
      });
    }
  };

  const handleBatchUpdate = async (option) => {
    const newStatus = option?.value;

    setTableOption({
      ...tableOption,
      isLoading: true,
    });

    const newValue = option.value;
    const finalData = {};
    finalData.status = newStatus;
    finalData.recipients = checkedItems;

    const oldValue = resendData[0]?.status;

    try {
      const checkbox = {};

      if (newValue === ALLOCATION_STATUS.APPROVED) {
        checkbox.html = checkboxPromptForEndorsingAllocations(
          oldValue,
          option.label,
          role
        );

        checkbox.preConfirm = () => {
          const checkbox1 = document.getElementById("swal-checkbox1")?.checked
            ? 1
            : 0;

          const checkbox2 = document.getElementById("swal-checkbox2")?.checked
            ? 1
            : 0;

          return {
            excludeCongAndCOS: checkbox1,
            showEndorsementToSpeaker: checkbox2,
          };
        };
        checkbox.focusConfirm = false;
      }

      const res = await Swal.fire({
        title: "Warning",
        // eslint-disable-next-line max-len
        text: `Are you sure you want to change status from ${oldValue} to ${option.label}?`,
        icon: "warning",
        confirmButtonText: "OK",
        showCancelButton: true,
        ...checkbox,
      });

      finalData.excludeCongAndCOS = Boolean(res.value?.excludeCongAndCOS);
      finalData.isSpeakerVisible = Boolean(res.value?.showEndorsementToSpeaker);

      if (res.isConfirmed) {
        if (newStatus === ALLOCATION_STATUS.APPROVED) {
          const endorsements = await Promise.all(
            resendData?.map(async (row) => {
              const details = { ...row };
              details.program = selectedProject?.Name;
              details.agency = agencyDetails?.name;
              details.agencyAcronym = agencyDetails?.acronym;

              let link = "";

              switch (id) {
                case "8":
                  link = await DohEndorsement(details);
                  break;

                case "28":
                  link = await ChedEndorsement(details);
                  break;

                case "11":
                  link = await DoleEndorsement(details);
                  break;

                case "16":
                  link = await DswdEndorsement(details);
                  break;

                case "27":
                  link = await TesdaEndorsement(details);
                  break;
                default:
                  break;
              }

              return {
                id: row?.id,
                endorsementLink: link,
              };
            })
          );

          finalData.endorsements = endorsements;
        }

        await batchUpdate({
          formData: finalData,
          callback: ({ isSuccess, msg }) => {
            if (isSuccess) {
              setCheckedItems([]);
              fetchData();

              if (SUPER_ADMIN_ACCOUNTS.includes(role)) {
                dispatch(fetchPendingProjects());
              }
              setSelectedStatus("");
              setResendData([]);

              const text = msg.replace(/\n/g, "<br>");

              Swal.fire({
                title: text.includes("<br>") ? "" : "Success!",
                icon: "success",
                html: text,
                customClass: text.includes("<br>")
                  ? { container: "sms-notif" }
                  : null,
              });
            } else {
              setCheckedItems([]);

              Toast({
                type: 4,
                content: "An error occurred!",
              });
            }
          },
        });
      }

      if (res.isDismissed) {
        setCheckedItems([]);
        setSelectedStatus("");
        setResendData([]);

        setTableOption({
          ...tableOption,
          isLoading: false,
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  const handleBumpIconOnClicked = async (congId, status) => {
    const result = await Swal.fire({
      title: "Warning",
      text: "Would you like to bump this allocation?",
      icon: "warning",
      confirmButtonText: "Yes",
      showCancelButton: true,
    });

    if (result.isConfirmed) {
      setTableOption({
        ...tableOption,
        isLoading: true,
      });

      toggleTimer(congId);

      genericPost({
        url: "/comment",
        formData: {
          text: "bumped this notification.",
          recipientId: congId,
        },
        callback: () => {
          //
        },
      });

      genericPost({
        url: "/recipient/bump-allocation-notification",
        formData: {
          status,
          recipientId: congId,
        },
        callback: ({ isSuccess }) => {
          if (isSuccess) {
            fetchData();
            fetchComments(congId);
          }
        },
      });
    }
  };

  const handleCloseModal = () => {
    toggleResendModal();
    setResendData([]);
    setCheckedItems([]);
    setSelectedStatus("");
  };

  const handleShowResendModal = () => {
    if (resendData.length !== 0) {
      if (checkedItems.length !== resendData.length) {
        const resendIDs = resendData.map((data) => data.id);

        const items = checkedItems.filter((item) => resendIDs.includes(item));
        setCheckedItems(items);

        Toast({
          type: 3,
          content: "All selected rows must have the same status.",
        });
      }

      setResendType("multiple");
      toggleResendModal();
    } else {
      Toast({
        type: 4,
        content: "Invalid selected rows.",
      });
      setCheckedItems([]);
    }
  };

  const hasProgramAccess = (progId) => {
    genericGet({
      url: `auth/has-access?program=${progId}`,
      callback: (response) => {
        if (!response.data) navigate("/404");
      },
    });
  };

  const columns = ProgramTable({
    id,
    checkedItems,
    tableOption,
    selectedStatus,
    setSelectedStatus,
    resendData,
    setResendData,
    setCheckedItems,
    setResendType,
    role,
    editingRowId,
    setTableOption,
    isEllipsisUsed,
    handleStatusChange,
    regions,
    setEditingCongressman,
    fetchComments,
    toggleModal,
    timer,
    navigate,
    programID,
    selectedProject,
    setIsResendModalShown,
    setMessageType,
    handleBumpIconOnClicked,
    handleDeleteAction,
  });

  useEffect(() => {
    hasProgramAccess(programID);
  }, [programID]);

  useEffect(() => {
    fetchAgency();
    fetchRegions();
  }, []);

  useEffect(() => {
    fetchData();
  }, [
    programID,
    tableOption.currentRowsPerPage,
    tableOption.offset,
    tableOption.searchValue,
    tableOption.sort.direction,
    tableOption.sort.field,
    selectedStatusFilter,
    selectedAllocationType,
  ]);

  useEffect(() => {
    if (selectedProject !== null) {
      fetchBudgetAllocation();
    }
  }, [selectedProject]);

  useEffect(() => {
    fetchData();

    if (selectedProject) fetchBudgetAllocation();
  }, [dateFrom]);

  if (!user) {
    return <LoadingScreen />;
  }

  return (
    <Container>
      <CardSection>
        <CardContainer>
          <FilterSection>
            {/* agency select dropdown here */}
            <CustomSelect
              isLoading={tableOption.isLoading}
              isDisabled={tableOption.isLoading}
              onChange={(option) => {
                setSelectedProject(option);
                navigate(`/projects/${id}/program/${option?.ID}`);
              }}
              value={selectedProject}
              options={selectOptions}
              styles={customProjectStyles}
            />
          </FilterSection>

          <EndorsementsAndBudget
            newTotalBudget={newTotalBudget}
            totalBudget={totalBudget}
            totalAmountSpent={totalAmountSpent}
            projects={projectBudget}
            remainingBalance={remainingBalance}
            handleOverallBudgetChange={handleOverallBudgetChange}
          />

          <ProgramFilters
            selectedStatusFilter={selectedStatusFilter}
            setSelectedStatusFilter={setSelectedStatusFilter}
            selectedAllocationType={selectedAllocationType}
            setSelectedAllocationType={setSelectedAllocationType}
            onChangeSearch={onChangeSearch}
            tableOption={tableOption}
            selectedStatus={selectedStatus}
            role={role}
            checkedItems={checkedItems}
            handleBatchUpdate={handleBatchUpdate}
            setMessageType={setMessageType}
            handleShowResendModal={handleShowResendModal}
            handleMultipleDelete={handleMultipleDelete}
          />

          {/* table here */}
          <TableSection>
            <CustomDataTable
              columns={columns}
              data={tableOption.data}
              noHeader
              onChangePage={onChangePage}
              onChangeRowsPerPage={onChangeRowsPerPage}
              onSort={onSort}
              paginationPerPage={tableOption.currentRowsPerPage}
              paginationServer
              paginationTotalRows={tableOption.totalRows}
              progressPending={tableOption.isLoading}
              sortServer
              defaultSortField={tableOption.sort.field}
              defaultSortAsc={tableOption.sort.direction}
            />
          </TableSection>
        </CardContainer>
        <CommentsModal
          isOpen={isModalShown}
          handleClose={toggleModal}
          comments={comments}
          congID={editingCongressman}
          fetchComments={fetchComments}
          programID={programID}
          handleFetchTable={fetchData}
        />
        <ResendModal
          isOpen={isResendModalShown}
          data={resendData}
          handleClose={handleCloseModal}
          type={resendType}
          messageType={messageType}
        />
      </CardSection>
    </Container>
  );
};

export default ProgramDetails;
